import cn from 'classnames';
import {FC, useEffect, useId, useRef, useState} from 'react';
import type {ForeignDocumentType, ForeignDocumentTypeOption} from '../../../model';
import styles from './DocumentTypeSelect.module.css';

type Props = {
	label?: string;
	value: ForeignDocumentType;
	options: ForeignDocumentTypeOption[];
	onChange: (value: ForeignDocumentType) => void;
	className?: string;
};

const ChevronIcon = ({up}: {up?: boolean}) => (
	<svg className={cn(styles.chevron, up && styles.chevron_up)} viewBox="0 0 24 24" fill="none" aria-hidden>
		<path
			d="M6 9l6 6 6-6"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

/** Выпадающий список типа документа (КЗ: паспорт / удостоверение). */
export const DocumentTypeSelect: FC<Props> = ({
	label = 'Тип документа',
	value,
	options,
	onChange,
	className,
}) => {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const listId = useId();
	const selected = options.find((option) => option.value === value) ?? options[0];

	useEffect(() => {
		if (!open) {
			return;
		}

		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('touchstart', handlePointerDown);

		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('touchstart', handlePointerDown);
		};
	}, [open]);

	return (
		<div className={cn(styles.root, className)} ref={rootRef}>
			<p className={styles.label}>{label}</p>
			<button
				type="button"
				className={styles.trigger}
				aria-expanded={open}
				aria-controls={listId}
				onClick={() => setOpen((current) => !current)}
			>
				<span className={styles.triggerText}>{selected?.label}</span>
				<ChevronIcon up={open} />
			</button>

			{open ? (
				<ul id={listId} className={styles.list} role="listbox">
					{options.map((option) => (
						<li key={option.value} className={styles.listItem} role="none">
							<button
								type="button"
								role="option"
								aria-selected={option.value === value}
								className={cn(styles.option, option.value === value && styles.optionSelected)}
								onClick={() => {
									onChange(option.value);
									setOpen(false);
								}}
							>
								{option.label}
							</button>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
};
